import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import moment from 'moment';
import { EditIcon, DeleteIcon } from 'components/mui';
import { CinchActionButtonText } from 'components';
const initials = name => name.split(' ').map(part => part[0]).join('').toUpperCase();

/**
 * The body of a notes section: the add form, the note cards and the empty state.
 * It has no header of its own, so a surface can place it inside its own section chrome.
 * `CinchNotes` renders it under its standard header.
 *
 * @param {object} notesApi - The return value of `useCinchNotes`.
 * @param {boolean} canEdit - Show the edit button on each note.
 * @param {boolean} canDelete - Show the delete button on each note.
 * @param {function} onDeleteRequest - Called with the note when delete is clicked. Defaults to deleting immediately.
 * @param {string} cardBackground - Background of each note card, for surfaces whose own background matches the default.
 */
export default function CinchNotesList({
  notesApi,
  canEdit = true,
  canDelete = true,
  onDeleteRequest,
  cardBackground = '#F9FBFD'
}) {
  const {
    notes,
    editingNoteId,
    editingText,
    setEditingText,
    isAddingNewNote,
    newNoteText,
    setNewNoteText,
    cancelAdding,
    startEditing,
    cancelEditing,
    handleCreateNote,
    handleUpdateNote,
    handleDeleteNote
  } = notesApi;
  const cardSx = {
    p: 2,
    background: cardBackground,
    borderRadius: '16px',
    position: 'relative'
  };
  return <Stack spacing={2}>
      {/* Add New Note Form */}
      {isAddingNewNote && <Box sx={cardSx}>
          <Stack spacing={2}>
            <TextField multiline rows={4} value={newNoteText} onChange={e => setNewNoteText(e.target.value)} variant="outlined" fullWidth placeholder="Enter your note..." autoFocus inputProps={{
          maxLength: 2000,
          style: {
            whiteSpace: 'pre-wrap'
          }
        }} helperText={!newNoteText.trim() ? 'Note must have content to save.' : `${newNoteText.length}/2000 characters`} />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <CinchActionButtonText size="small" text="Cancel" onClick={cancelAdding} sx={{
            borderRadius: '20px'
          }} />
              <CinchActionButtonText size="small" text="Save" disabled={!newNoteText.trim()} onClick={handleCreateNote} />
            </Stack>
          </Stack>
        </Box>}

      {/* Existing Notes */}
      {notes && notes.length > 0 && notes.map(note => <Box key={note.id} sx={cardSx}>
            {editingNoteId === note.id ?
      // Edit Mode
      <Stack spacing={2}>
                <TextField multiline rows={4} value={editingText} onChange={e => setEditingText(e.target.value)} variant="outlined" fullWidth placeholder="Enter your note..." inputProps={{
          maxLength: 2000,
          style: {
            whiteSpace: 'pre-wrap'
          }
        }} helperText={!editingText.trim() ? 'Note must have content to save.' : `${editingText.length}/2000 characters`} />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <CinchActionButtonText size="small" text="Cancel" onClick={cancelEditing} sx={{
            borderRadius: '20px'
          }} />
                  <CinchActionButtonText size="small" text="Save" disabled={!editingText.trim()} onClick={() => handleUpdateNote(note.id)} />
                </Stack>
              </Stack> :
      // Display Mode
      <Stack spacing={2}>
                <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{
              width: 32,
              height: 32
            }}>
                      {note.edited_by_name ? initials(note.edited_by_name) : note.created_by_name ? initials(note.created_by_name) : 'U'}
                    </Avatar>
                    <Stack spacing={0} sx={{
              color: 'text.secondary'
            }}>
                      {note.edited_by_name ? <>
                          <Typography variant="caption" sx={{
                  lineHeight: 1.2
                }}>
                            Edited by: {note.edited_by_name}
                          </Typography>
                          {note.updated_when && <Typography variant="caption" sx={{
                  lineHeight: 1.2
                }}>
                              Edited: {moment(note.updated_when).local().format('MMM D, YYYY h:mm A')}
                            </Typography>}
                          {note.created_by_name && note.created_by_name !== note.edited_by_name && <Typography variant="caption" sx={{
                  lineHeight: 1.2
                }}>
                              Created by: {note.created_by_name}
                            </Typography>}
                        </> : <>
                          <Typography variant="caption" sx={{
                  lineHeight: 1.2
                }}>
                            Created by: {note.created_by_name}
                          </Typography>
                          {note.created_when && <Typography variant="caption" sx={{
                  lineHeight: 1.2
                }}>
                              Created: {moment(note.created_when).local().format('MMM D, YYYY h:mm A')}
                            </Typography>}
                        </>}
                    </Stack>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {canEdit && <IconButton aria-label="Edit note" onClick={e => {
              e.stopPropagation();
              startEditing(note);
            }} sx={{
              'color': 'text.secondary',
              'padding': '6px',
              '&:hover': {
                color: 'primary.main',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}>
                        <EditIcon sx={{
                fontSize: '20px'
              }} />
                      </IconButton>}
                    {canDelete && <IconButton aria-label="Delete note" onClick={e => {
              e.stopPropagation();
              if (onDeleteRequest) {
                onDeleteRequest(note);
              } else {
                handleDeleteNote(note.id);
              }
            }} sx={{
              'color': 'text.secondary',
              'padding': '6px',
              '&:hover': {
                color: 'error.main',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}>
                        <DeleteIcon sx={{
                fontSize: '20px'
              }} />
                      </IconButton>}
                  </Stack>
                </Box>
                <Typography variant="body1" sx={{
          fontWeight: 'medium',
          whiteSpace: 'pre-wrap'
        }}>
                  {note.note}
                </Typography>
              </Stack>}
          </Box>)}

      {/* No Notes Message */}
      {(!notes || notes.length === 0) && !isAddingNewNote && <Box sx={{
      p: 2,
      background: cardBackground,
      borderRadius: '16px'
    }}>
          <Typography>No notes provided.</Typography>
        </Box>}
    </Stack>;
}
CinchNotesList.propTypes = {
  /** The return value of `useCinchNotes`. */
  notesApi: PropTypes.object.isRequired,
  /** Show the edit button on each note. */
  canEdit: PropTypes.bool,
  /** Show the delete button on each note. */
  canDelete: PropTypes.bool,
  /** Called with the note when delete is clicked; defaults to deleting immediately. */
  onDeleteRequest: PropTypes.func,
  /** Background of each note card. */
  cardBackground: PropTypes.string
};